import { CampaignCategoryStats } from '@/types';
import { getChartColor } from '@/utils/colorUtils';
import { useCallback, useEffect, useRef, useMemo } from 'react';

interface ChartDataPoint {
  name: string;
  value: number;
  currency: string | undefined;
  color: string;
}

interface UseChartInteractionProps {
  data: Record<string, CampaignCategoryStats>;
  selectedSegment: string | null;
  setSelectedSegment: (segment: string | null) => void;
}

const useChartInteraction = ({
  data,
  selectedSegment,
  setSelectedSegment,
}: UseChartInteractionProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const chartData: ChartDataPoint[] = useMemo(
    () =>
      Object.entries(data).map(([name, stats], index) => ({
        name,
        value: stats.total.amount,
        currency: stats.total.currency,
        color: getChartColor(index % Object.keys(data).length),
      })),
    [data]
  );

  const handleClickOutside = useCallback(
    (event: MouseEvent | TouchEvent) => {
      const cells = containerRef.current?.querySelectorAll('.recharts-pie-sector');
      const clickedOnCell = Array.from(cells || []).some((cell) =>
        cell.contains(event.target as Node)
      );

      if (!clickedOnCell && selectedSegment !== null) {
        setSelectedSegment(null);
      }
    },
    [selectedSegment, setSelectedSegment]
  );

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [handleClickOutside]);

  const handleClick = useCallback(
    (e: ChartDataPoint) => {
      setSelectedSegment(e.name === selectedSegment ? null : e.name);
    },
    [setSelectedSegment, selectedSegment]
  );

  return {
    containerRef,
    chartData,
    handleClick,
  };
};

export default useChartInteraction;
